import {
  ConsignmentApiModel,
  Dimensions,
  MarketplaceOrderApiModel,
  SmartSendItemApiModel
} from '~/lib/api/smartsend/types';
import {
  BuyerNote,
  DeliveryInstructions,
  DeliverySafePlace,
  EoriNumber,
  ExportReason,
  GSP,
  IossNumber,
  MarketplaceService,
  Prime
} from '../../lib/consts/ItemMetaConstants';
import { SumNumber } from '../../lib/helpers/ArrayHelper';
import { toIso3Code } from '../../lib/helpers/IsoCodeHelper';
import { postcodeHelper } from '../../lib/helpers/PostcodeHelper';
import { GetFieldData, GetFieldDataExtra, GetHeaders, GetMatchedHeaderName } from './GetFieldData';
import { LocalUnitsEntry, toMetricDimensionCm, toMetricWeightKg } from '../../lib/helpers/DimensionUnits';
import { reconcileLocalUnitsEntry } from '../../lib/helpers/LocalUnitsEntryHelper';
import {
  CoverExtra,
  DeliveredDutyPaidExtra,
  DeliveryGuaranteeExtra,
  PrintInStoreExtra,
  SignatureExtra,
  SmsExtra
} from '../ExtrasHelper';
import { FindCountryName } from '../../lib/cache/CountryCache';
import { SmartSendProvider } from '~/lib/consts/providers';
import { GenerateConsignmentFromItem } from '../smartsend/panels/consignments/GenerateConsignmentFromItem';

function sanitiseNumber(v: any, d: number): number {
  if (!v || v === 'NULL') {
    return d;
  }
  try {
    return parseFloat(v);
  } catch {
    return d;
  }
}

export interface CsvSmartSendMapResult {
  raw: string[];
  item: SmartSendItemApiModel;
}

export function MapCsvDataToSmartSendItem(
  data: Array<any>,
  defaultCountry: string,
  websiteUnits: 'imperial' | 'metric'
): CsvSmartSendMapResult[] {
  const output: CsvSmartSendMapResult[] = [];
  const headers = GetHeaders(data);

  for (let index = 1; index < data.length; index++) {
    const rowData = data[index].data;

    const country = GetFieldData('country', headers, rowData) || defaultCountry;
    const contents = GetFieldData('item name', headers, rowData) || GetFieldData('reference', headers, rowData);
    const ref = GetFieldData('reference', headers, rowData);

    const rawWeight = sanitiseNumber(GetFieldData('weight', headers, rowData), 0);
    const rawHeight = sanitiseNumber(GetFieldData('height', headers, rowData), 0);
    const rawWidth = sanitiseNumber(GetFieldData('width', headers, rowData), 0);
    const rawLength = sanitiseNumber(GetFieldData('length', headers, rowData), 0);

    // The matched weight column's own header can override the sheet-wide `units` setting: a
    // header literally named/containing "kg" is always metric, and "lb" is always imperial -
    // independent of what the rest of the sheet is in. Any other weight header (weight, wght,
    // Item Weight) keeps following `units`, same as length/width/height (unaffected below).
    const weightHeaderName = GetMatchedHeaderName('weight', headers)?.toLowerCase() ?? '';
    const weightIsImperial = weightHeaderName.includes('kg')
      ? false
      : weightHeaderName.includes('lb')
        ? true
        : websiteUnits === 'imperial';

    const metricDimensions: Dimensions = {
      Weight: weightIsImperial ? toMetricWeightKg(rawWeight) : rawWeight,
      Height: websiteUnits === 'imperial' ? toMetricDimensionCm(rawHeight) : rawHeight,
      Width: websiteUnits === 'imperial' ? toMetricDimensionCm(rawWidth) : rawWidth,
      Length: websiteUnits === 'imperial' ? toMetricDimensionCm(rawLength) : rawLength
    };

    // Record what the customer actually put in the CSV (in their own unit system) so it displays
    // as an as-entered imperial value rather than being silently re-derived from the rounded metric
    // conversion above - same as a manual imperial entry. Fields left blank in the CSV (0) aren't
    // "as entered", so they're omitted rather than recorded as a bogus 0 original. The entry is only
    // ever recorded when the sheet's overall unit setting is imperial - a "lb"-named weight column
    // still converts correctly even on a metric-flagged sheet, but isn't recorded as as-entered
    // unless the user actually selected imperial for the import.
    const localUnitsEntry: LocalUnitsEntry | undefined =
      websiteUnits === 'imperial'
        ? {
            unitSystem: 'imperial',
            originalWeightLb: weightIsImperial ? rawWeight || undefined : undefined,
            originalLengthIn: rawLength || undefined,
            originalWidthIn: rawWidth || undefined,
            originalHeightIn: rawHeight || undefined
          }
        : undefined;

    const recipient = postcodeHelper({
      CountryIsoCode: country.length == 2 ? toIso3Code(country) : country,
      Name:
        GetFieldData('customer', headers, rowData) ||
        `${GetFieldData('first name', headers, rowData)} ${GetFieldData('last name', headers, rowData)}`,
      Email: GetFieldData('email', headers, rowData),
      Phone: GetFieldData('telephone', headers, rowData),
      AddressLine1: GetFieldData('property', headers, rowData),
      AddressLine2: GetFieldData('street', headers, rowData),
      Locality: GetFieldData('locality', headers, rowData),
      Town: GetFieldData('town', headers, rowData) || GetFieldData('street', headers, rowData),
      County: GetFieldData('county', headers, rowData),
      VatNumber: GetFieldData('vatnumber', headers, rowData),
      Postcode: GetFieldData('postcode', headers, rowData),
      Organisation: GetFieldData('organisation', headers, rowData),
      Meta: {},
      Provider: SmartSendProvider
    });
    recipient.PropertyKey = `${recipient.AddressLine1}|${recipient.AddressLine2}|${recipient.Postcode}|${recipient.CountryIsoCode}`;

    const item = {
      Status: 'Active',
      MarketplaceStatus: 'Sold',
      Recipient: recipient,
      Tags: (GetFieldData('tags', headers, rowData)?.split(',') || []).filter((x) => !!x),
      Sender: { Meta: {}, VatNumber: GetFieldData('sender-vatnumber', headers, rowData) },
      PackageDimensions: metricDimensions,
      Source: { Name: 'CSV', Id: 'csv' },
      Description: contents,
      MarketplaceOrderLines: [
        {
          Source: { Name: 'CSV', Id: 'csv' },
          MarketplaceOrderId: ref == contents ? '' : ref,
          CustomerReference: ref == contents ? '' : ref,
          InternalReference: ref == contents ? '' : ref,
          Quantity: sanitiseNumber(GetFieldData('quantity', headers, rowData), 1),
          Value: sanitiseNumber(GetFieldData('value', headers, rowData), 20),
          Product: {
            Description: contents,
            TariffCode: GetFieldData('tariffCode', headers, rowData),
            Meta: {},
            CountryOfManufacture: FindCountryName(GetFieldData('origincountry', headers, rowData)),
            Sku: GetFieldData('sku', headers, rowData)
          },
          Meta: {}
        } as MarketplaceOrderApiModel
      ] as MarketplaceOrderApiModel[],
      Meta: {},
      Extras: []
    } as SmartSendItemApiModel;

    // Email
    if (item.Recipient?.Email) {
      item.Recipient.Email = item.Recipient.Email.toLowerCase();
    }
    if (item.Sender?.Email) {
      item.Sender.Email = item.Sender.Email.toLowerCase();
    }

    // Assign Sum
    item.Value = SumNumber(item.MarketplaceOrderLines.map((x) => x.Value));

    // Extras
    if (GetFieldDataExtra('sms', headers, rowData)) {
      item.Extras.push({ Id: SmsExtra, Name: 'Sms' });
    }

    if (GetFieldDataExtra('cover', headers, rowData)) {
      item.Extras.push({ Id: CoverExtra, Name: 'Cover' });
    }

    if (GetFieldDataExtra('signature', headers, rowData)) {
      item.Extras.push({ Id: SignatureExtra, Name: 'Signature' });
    }

    if (GetFieldDataExtra('delivery guarantee', headers, rowData)) {
      item.Extras.push({ Id: DeliveryGuaranteeExtra, Name: 'Delivery Guarantee' });
    }

    if (GetFieldDataExtra('delivered duty paid', headers, rowData)) {
      item.Extras.push({ Id: DeliveredDutyPaidExtra, Name: 'Delivered Duty Paid' });
    }

    if (GetFieldDataExtra('pis', headers, rowData)) {
      item.Extras.push({ Id: PrintInStoreExtra, Name: 'Print in Store' });
    }

    // Only store if the website is set to imperial - dimensions are still as-entered imperial even
    // when a "kg"-named weight column keeps the weight itself metric (localUnitsEntry above already
    // omits originalWeightLb in that case).
    if (websiteUnits === 'imperial') {
      reconcileLocalUnitsEntry(item.Meta, localUnitsEntry);
    }

    // GSP
    if (GetFieldDataExtra('gsp', headers, rowData)) {
      item.Meta[GSP] = { Value: GetFieldData('gsp', headers, rowData), Type: 'bool' };
    }

    // Amazon Prime
    if (GetFieldDataExtra('prime', headers, rowData)) {
      item.Meta[Prime] = { Value: GetFieldData('prime', headers, rowData), Type: 'bool' };
    }

    // Recipient Specifics
    item.Recipient.Meta[EoriNumber] = { Value: GetFieldData('eori', headers, rowData), Type: 'string' };

    // Sender Specifics
    item.Sender.Meta[EoriNumber] = { Value: GetFieldData('sender-eori', headers, rowData), Type: 'string' };

    // Instuctions
    item.Meta[DeliveryInstructions] = { Value: GetFieldData('instructions', headers, rowData), Type: 'string' };
    item.Meta[DeliverySafePlace] = { Value: GetFieldData('safe place', headers, rowData), Type: 'string' };
    item.Meta[BuyerNote] = { Value: GetFieldData('buyer note', headers, rowData), Type: 'string' };

    // Service
    const s = GetFieldData('service', headers, rowData);
    if (s) {
      item.Meta[MarketplaceService] = { Value: s, Type: 'string' };
    }

    // Label Reference
    const lr = GetFieldData('labelreference', headers, rowData);
    if (lr) {
      item.Meta['LabelReference'] = { Value: lr, Type: 'string' };
    }

    // Item Specifics
    item.Meta[IossNumber] = { Value: GetFieldData('iossnumber', headers, rowData), Type: 'string' };
    item.Meta[ExportReason] = { Value: GetFieldData('exportreason', headers, rowData) || 'Sale', Type: 'string' };

    output.push({ item: item, raw: rowData });
  }

  return output;
}

/**
 * Groups the rows of a CSV by recipient, and where the customer asked for more than one parcel, builds
 * the consignment that splits their orders across those parcels.
 *
 * `propertyKeyConsignment` holds an arrangement the customer has already made by hand - dragging an
 * order from one parcel card to another in the preview. It has to be passed in rather than derived,
 * because this function deep-clones its input and rebuilds the plan from the order lines every time it
 * runs: without somewhere outside to keep it, a dragged arrangement would be recomputed away on the
 * next render, and every rebuild mints fresh row UIDs so the rows would not even be addressable.
 *
 * A stored arrangement is used AS-IS, by reference. Re-splitting it would defeat the point, and reusing
 * the object is what keeps row UIDs stable between renders.
 */
export function CombineCsvSmartSendMapResults(
  results: CsvSmartSendMapResult[],
  // Both default to empty so the plain "just group by recipient" call stays a one-argument one. No count
  // means no consignment, which is the behaviour that predates the parcel-count dropdown.
  propertyKeyConsignmentCount: Record<string, number> = {},
  propertyKeyConsignment: Record<string, ConsignmentApiModel> = {}
): CsvSmartSendMapResult[] {
  const raw = JSON.parse(JSON.stringify(results)) as CsvSmartSendMapResult[];

  // Set aside the items that don't have a recipient property key (i.e. they can't be grouped) and will be output as-is.
  const output: CsvSmartSendMapResult[] = raw.filter((x) => !x.item?.Recipient?.PropertyKey);

  // Group the remaining items by recipient property key, then combine them into a single SmartSendItemApiModel with all MarketplaceOrderLines.
  const groupByRecipientPropertyKey = raw
    .filter((x) => x.item?.Recipient?.PropertyKey)
    .reduce(
      (acc, curr) => {
        const key = curr.item.Recipient.PropertyKey;
        if (!acc[key]) {
          acc[key] = [];
        }
        acc[key].push(curr);
        return acc;
      },
      {} as Record<string, CsvSmartSendMapResult[]>
    );

  for (const key in groupByRecipientPropertyKey) {
    const group = groupByRecipientPropertyKey[key];
    if (group.length === 1) {
      // Recipient only has one item.
      output.push(group[0]);
    } else {
      // Recipient has multiple items, combine them into one SmartSendItemApiModel with all MarketplaceOrderLines.
      const combinedItem = group.reduce(
        (acc, curr) => {
          acc.item.MarketplaceOrderLines.push(...curr.item.MarketplaceOrderLines);
          return acc;
        },
        {
          item: {
            ...group[0].item,
            Source: { Id: 'grouped', Name: 'Grouped', AccountId: SmartSendProvider },
            MarketplaceOrderLines: []
          },
          raw: group[0].raw
        }
      );

      // Sum Value of all MarketplaceOrderLines and assign to combinedItem.item.Value
      combinedItem.item.Value = SumNumber(group.map((i) => i.item.Value || 0));

      // Sum weight from all PackageDimensions.Weight and assign to combinedItem.item.PackageDimensions.Weight
      combinedItem.item.PackageDimensions.Weight = SumNumber(group.map((i) => i.item.PackageDimensions.Weight || 0));

      // Allows the user to select how many boxes they are using and creates a consignment if needed.
      // One parcel deliberately stores NO consignment: a one-parcel consignment is not the same state as
      // a single-parcel shipment, and would be persisted and quoted as a consignment.
      if (propertyKeyConsignmentCount[key] && propertyKeyConsignmentCount[key] > 1) {
        combinedItem.item.Consignment =
          propertyKeyConsignment[key] ??
          GenerateConsignmentFromItem(
            combinedItem.item,
            combinedItem.item.PackageDimensions,
            propertyKeyConsignmentCount[key]
          );
      }

      output.push(combinedItem);
    }
  }

  return output;
}
